import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { NavLink, useNavigate } from "react-router-dom";
import CityDropdown from "../HomeComonents/CityDropdown";
import toast from "react-hot-toast";
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";

const MemberShipFormSumUp = () => {
    const [formData, setFormData] = useState({
        titleid: '',
        fullname: "",
        phoneNo: "",
        email: "",
        membershipCategory: "0",
        businessName: "",
        businessAddressLine1: "",
        businessAddressLine2: "",
        city: "",
        postcode: "",
        countryCode: "+44",
        consent: false,
        reCaptcha: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [checkoutId, setCheckoutId] = useState(null);
    const [fees, setFees] = useState({ msb: 199.00, affiliate: 799.00 });
    const navigate = useNavigate();

    // Fetch fees on component mount
    useEffect(() => {
        const fetchFees = async () => {
            try {
                const response = await axios.get("https://msb-payment-api.vercel.app/api/membership-fees");
                if (response.data) {
                    setFees(response.data);
                }
            } catch (error) {
                console.error("Error fetching fees:", error);
            }
        };
        fetchFees();
    }, []);

    // Framer motion animation settings for heading
    const headingVariants = {
        hidden: { opacity: 0, x: 50 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.1,
                type: "spring",
                stiffness: 120,
            },
        }),
    };

    const headingText = "Membership & Payment";

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleCheckBoxMemberChange = (e) => {
        const { value } = e.target;
        setFormData((prev) => ({
            ...prev,
            membershipCategory: value,
        }));
        setErrors((prev) => ({ ...prev, membershipCategory: "" }));
    };

    const handleReCaptcha = (value) => {
        setFormData((prev) => ({ ...prev, reCaptcha: value }));
        setErrors((prev) => ({ ...prev, reCaptcha: "" }));
    };

    const isValidUKPostcode = (postcode) => {
        const ukPostcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}|GIR 0AA)$/i;
        return ukPostcodeRegex.test(postcode);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.fullname) newErrors.fullname = "Full Name is required.";
        if (!formData.titleid) newErrors.titleid = "Title is required";
        if (!formData.phoneNo) newErrors.phoneNo = "Phone No is required.";
        if (!formData.email) newErrors.email = "Email is required.";
        if (!formData.businessName) newErrors.businessName = "Business Name is required.";
        if (!formData.businessAddressLine1) newErrors.businessAddressLine1 = "Business Address Line 1 is required.";
        if (!formData.city) newErrors.city = "City is required.";
        if (!formData.postcode) {
            newErrors.postcode = "Postcode is required.";
        } else if (!isValidUKPostcode(formData.postcode)) {
            newErrors.postcode = "Please enter a valid UK postcode.";
        }
        if (!formData.consent) newErrors.consent = "Please accept the Privacy Policy.";
        if (!formData.reCaptcha) newErrors.reCaptcha = "Please verify that you are not a robot.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCheckboxChange = (e) => {
        const { name, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : e.target.value,
        }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    }



    const trimFormData = (data) => {
        const trimmedData = {};
        Object.keys(data).forEach((key) => {
            const value = data[key];
            trimmedData[key] = typeof value === "string" ? value.trim() : value;
        });
        return trimmedData;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("Submitting Membership Form...", formData);
        if (!validateForm()) {
            console.log("Validation Failed:", errors);
            return;
        }

        window.grecaptcha?.reset();
        setLoading(true);
        let toastId = toast.loading(<b>Processing Registration...</b>);

        try {
            const trimmedFormData = trimFormData(formData);

            // 1. Register User in Local DB
            const regResponse = await axios.post(
                "https://msb-payment-api.vercel.app/api/register",
                trimmedFormData
            );

            if (regResponse.data.response) {
                const userId = regResponse.data.data._id;
                toast.success(<b>Registration Success! Initiating Payment...</b>, { id: toastId });

                // 2. Create SumUp Checkout
                const checkoutResponse = await axios.post("https://msb-payment-api.vercel.app/api/create-checkout", {
                    userId: userId,
                    membershipCategory: formData.membershipCategory,
                    amount: formData.membershipCategory === "0" ? fees.msb : fees.affiliate,
                    currency: 'GBP',
                    email: formData.email
                });

                if (checkoutResponse.data.response) {
                    const { checkoutId: cId, checkoutUrl, isMock: mockFlag } = checkoutResponse.data;
                    console.log("Checkout Response:", checkoutResponse.data); // Debugging

                    setCheckoutId(cId);
                    // Robust check: if backend says it's mock OR if the ID starts with MOCK
                    const actualMockState = mockFlag || (cId && cId.startsWith('MOCK-'));

                    const finalCheckoutUrl = checkoutUrl;

                    if (finalCheckoutUrl && !actualMockState) {
                        toast.success(<b>Redirecting to Secure Payment...</b>, { id: toastId });
                        setTimeout(() => {
                            window.location.href = finalCheckoutUrl;
                        }, 1500);
                    } else if (actualMockState) {
                        toast.error(<b>Mock payment is disabled. Please use live credentials.</b>, { id: toastId });
                    } else {
                        toast.success(<b>Checkout Ready! Please enter your card details.</b>, { id: toastId });
                    }
                } else {
                    toast.error(<b>Failed to create checkout</b>, { id: toastId });
                }
            } else {
                toast.error(regResponse.data.message || "Something went wrong.", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Something went wrong.", { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (checkoutId && window.SumUpCard) {
            const timer = setTimeout(() => {
                window.SumUpCard.mount({
                    checkoutId: checkoutId,
                    onResponse: async function (type, body) {
                        console.log('SumUp Response:', type, body);
                        if (type === 'success' || (body && body.status === 'PAID')) {
                            toast.success(<b>Payment Successful! Finalizing Membership...</b>);

                            try {
                                const trimmedFormData = trimFormData(formData);
                                const response = await axios.post(
                                    "https://msbassociationuk.org/prod/api/msb/savemembershipdetails",
                                    trimmedFormData
                                );

                                if (response.data.response && response.data.responseCode === "00") {
                                    toast.success(<b>Membership details saved successfully!</b>);
                                    setTimeout(() => navigate("/member-success"), 1000);
                                } else {
                                    toast.error(response.data.data || "External API submission failed.");
                                }
                            } catch (err) {
                                console.error("External API error:", err);
                                toast.error("Failed to submit to external API.");
                            }
                        } else {
                            toast.error(<b>Payment Failed or Cancelled</b>);
                        }
                    }
                });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [checkoutId, formData, navigate]);


    return (
        <div>
            <section className="job_apply_section section_space">
                <div style={{ maxWidth: '850px', margin: '0 auto' }}>
                    <div className="job_apply_form" style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '15px', padding: '40px' }}>
                        <div className="heading_block text-center mb-5">
                            <h3 className="heading_text" style={{ color: '#1a1a1a' }}>
                                {headingText}
                            </h3>
                            <p className="heading_description mb-0">
                                Register and pay your membership fee securely via SumUp.
                            </p>
                        </div>

                        {!checkoutId ? (
                            <form onSubmit={handleSubmit}>
                                {/* Business Information Section */}
                                <div className="card mb-4 shadow-sm">
                                    <h5 className="card-header bg-white">Business Information</h5>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className=" mt-3">
                                                <FormControl component="fieldset" className="form-group w-100 ">
                                                    <label component="legend" className="input_title ">
                                                        Select Your Membership Type <sup>*</sup>
                                                    </label>
                                                    <FormGroup>
                                                        <div className="row mt-2 mb-2">
                                                            <div className="col-6">
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={formData.membershipCategory === "0"}
                                                                            onChange={handleCheckBoxMemberChange}
                                                                            name="membershipCategory"
                                                                            value="0"
                                                                        />
                                                                    }
                                                                    label="MSB Member"
                                                                />
                                                            </div>
                                                            <div className="col-6">
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={formData.membershipCategory === "1"}
                                                                            onChange={handleCheckBoxMemberChange}
                                                                            name="membershipCategory"
                                                                            value="1"
                                                                        />
                                                                    }
                                                                    label="Affiliate Member"
                                                                />
                                                            </div>
                                                        </div>
                                                    </FormGroup>
                                                </FormControl>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="form-group">
                                                    <label className="input_title" htmlFor="businessName">Business Name<sup>*</sup></label>
                                                    <input
                                                        id="businessName"
                                                        className={`form-control ${errors.businessName ? "is-invalid" : ""}`}
                                                        type="text"
                                                        name="businessName"
                                                        placeholder="Enter your Business Name"
                                                        value={formData.businessName}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.businessName && <span className="text-danger">{errors.businessName}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-12 mt-3">
                                                <div className="form-group">
                                                    <label className="input_title" htmlFor="businessAddressLine1">Address Line 1<sup>*</sup></label>
                                                    <input
                                                        id="businessAddressLine1"
                                                        className={`form-control ${errors.businessAddressLine1 ? "is-invalid" : ""}`}
                                                        type="text"
                                                        name="businessAddressLine1"
                                                        placeholder="Enter Address Line 1"
                                                        value={formData.businessAddressLine1}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.businessAddressLine1 && <span className="text-danger">{errors.businessAddressLine1}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-12 mt-3">
                                                <div className="form-group">
                                                    <label className="input_title" htmlFor="businessAddressLine2">Address Line 2</label>
                                                    <input
                                                        id="businessAddressLine2"
                                                        className="form-control"
                                                        type="text"
                                                        name="businessAddressLine2"
                                                        placeholder="Enter Address Line 2"
                                                        value={formData.businessAddressLine2}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.businessAddressLine2 && <span className="text-danger">{errors.businessAddressLine2}</span>}
                                                </div>
                                            </div>

                                            <CityDropdown formData={formData} setFormData={setFormData} errors={errors} />

                                            <div className="col-md-6 mt-3">
                                                <div className="form-group">
                                                    <label className="input_title" htmlFor="postcode">Postcode<sup>*</sup></label>
                                                    <input
                                                        id="postcode"
                                                        className={`form-control ${errors.postcode ? "is-invalid" : ""}`}
                                                        type="text"
                                                        name="postcode"
                                                        placeholder="Enter Postcode"
                                                        value={formData.postcode}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.postcode && <span className="text-danger">{errors.postcode}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Personal Information Section */}
                                <div className="card mb-4 shadow-sm">
                                    <h5 className="card-header bg-white">Personal Information</h5>
                                    <div className="card-body mt-3">
                                        <div className="row">
                                            <div className="col-md-3 col-12">
                                                <div className="form-group">
                                                    <label className="input_title mt-3" htmlFor="titleid">Title<sup>*</sup></label>
                                                    <select
                                                        id="titleid"
                                                        className={`form-control ${errors.titleid ? "is-invalid" : ""}`}
                                                        name="titleid"
                                                        value={formData.titleid}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="1">Mr.</option>
                                                        <option value="2">Mrs.</option>
                                                        <option value="3">Miss</option>
                                                        <option value="4">Dr.</option>
                                                    </select>
                                                    {errors.titleid && <span className="text-danger">{errors.titleid}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-9">
                                                <div className="form-group">
                                                    <label className="input_title mt-3" htmlFor="fullname">Full Name<sup>*</sup></label>
                                                    <input
                                                        id="fullname"
                                                        className={`form-control ${errors.fullname ? "is-invalid" : ""}`}
                                                        type="text"
                                                        name="fullname"
                                                        placeholder="Enter your Full Name"
                                                        value={formData.fullname}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.fullname && <span className="text-danger">{errors.fullname}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-12 mt-3">
                                                <div className="form-group">
                                                    <label className="input_title" htmlFor="phoneNo">Phone<sup>*</sup></label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">+44</span>
                                                        <input
                                                            className={`form-control ${errors.phoneNo ? "is-invalid" : ""}`}
                                                            type="text"
                                                            name="phoneNo"
                                                            placeholder="Enter Phone Number"
                                                            value={formData.phoneNo}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                    {errors.phoneNo && <span className="text-danger">{errors.phoneNo}</span>}
                                                </div>
                                            </div>
                                            <div className="col-md-12 mt-3">
                                                <div className="form-group">
                                                    <label className="input_title" htmlFor="email">Email Address<sup>*</sup></label>
                                                    <input
                                                        id="email"
                                                        className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                                        type="email"
                                                        name="email"
                                                        placeholder="Enter your Email Address"
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                    />
                                                    {errors.email && <span className="text-danger">{errors.email}</span>}
                                                </div>
                                            </div>



                                            <div className="form-group mb-4 mt-4">
                                                <div className="form-check ">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="consent"
                                                        name="consent"
                                                        checked={formData.consent}
                                                        onChange={handleCheckboxChange}
                                                    />
                                                    <label className="form-check-label" htmlFor="consent">
                                                        I consent to the processing of my data and agree to pay the registration fee.
                                                    </label>
                                                </div>
                                                {errors.consent && <span className="text-danger">{errors.consent}</span>}
                                            </div>

                                            {/* reCAPTCHA */}
                                            <div className="col-md-12 mt-3 mb-3 d-flex justify-content-center">
                                                <div>
                                                    <ReCAPTCHA
                                                        sitekey="6LfmsZoqAAAAAPQgbaeEuX7w8IZxz7Ncqgs2i0hY"
                                                        onChange={handleReCaptcha}
                                                    />
                                                    {errors.reCaptcha && <div className="text-danger text-center mt-1">{errors.reCaptcha}</div>}
                                                </div>
                                            </div>

                                            <div className="col-md-12">
                                                <div className="text-center mt-4">
                                                    <button type="submit" className="btn btn-primary btn-lg px-5" disabled={loading}>
                                                        {loading ? "Processing..." : `Register & Pay £${formData.membershipCategory === "0" ? fees.msb : fees.affiliate}`}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        ) : (
                            <div className="text-center">
                                <h4>Complete Your Payment</h4>
                                <p>Please enter your card details below to complete the registration.</p>

                                <div className="p-4 border rounded bg-white mb-4">
                                    <div id="sumup-card-container" style={{ minHeight: '100px' }}>
                                        <p className="text-muted">Loading secure payment field...</p>
                                    </div>
                                </div>

                                <button className="btn btn-outline-secondary mt-3" onClick={() => { setCheckoutId(null); }}>
                                    Go Back
                                </button>
                            </div>
                        )}

                    </div>
                </div>
            </section>
        </div>
    );
};

export default MemberShipFormSumUp;
